import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { withTimeout } from "@/hooks/useLoadState";
import { organizationApi } from "../../../../services/phase1Api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PhoneField } from "@/components/PhoneField";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Building2, Loader2, Search, Pencil, UserCog, Ban, GitMerge, X, Save,
  School, Users, ClipboardCheck, ArrowLeft, Activity,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS = {
  active: "bg-emerald-100 text-emerald-700", inactive: "bg-slate-100 text-slate-500",
  merged: "bg-amber-100 text-amber-700", "Quote Created": "bg-blue-100 text-blue-700",
};

const Field = ({ label, children }) => (
  <div>
    <label className="text-[11.5px] uppercase tracking-wider font-semibold text-foreground/50 block mb-1">{label}</label>
    {children}
  </div>
);

export default function OrganizationsAdminPage({ session }) {
  const [rows, setRows] = useState(null);
  const [types, setTypes] = useState([]);
  const [q, setQ] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [detail, setDetail] = useState(null);      // org id being viewed/edited
  const [detailData, setDetailData] = useState(null);
  const [form, setForm] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [mergeMode, setMergeMode] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => {
    setRows(null);
    withTimeout(organizationApi.list({ q, type: typeFilter }, session))
      .then((d) => { setRows(d.organizations); setTypes(d.types || []); })
      .catch(() => { setRows([]); toast.error("Failed to load organizations (request may have timed out) — use Refresh to retry."); });
  };
  useEffect(load, [session]); // eslint-disable-line
  useEffect(() => {
    axios.get(`${API}/api/employees/`, { headers: { "x-user-id": session.user.id, "x-user-role": session.user.role, "x-tenant-id": session.tenant?.id || "tenant_1" } })
      .then((r) => setEmployees(Array.isArray(r.data) ? r.data : (r.data.employees || [])))
      .catch(() => {});
  }, [session]);

  const openDetail = async (id) => {
    setDetail(id); setDetailData(null); setMergeMode(false);
    try {
      const d = await organizationApi.get(id, session);
      setDetailData(d);
      setForm({
        name: d.name || "", organization_type: d.organization_type || "other",
        state: d.state || "", website: d.website || "", phone: d.phone || "",
        address: d.address || "", primary_contact_name: d.primary_contact_name || "",
        primary_contact_email: d.primary_contact_email || "", primary_contact_phone: d.primary_contact_phone || "",
        status: d.status || "active", assigned_sales_employee_id: d.assigned_sales_employee_id || "",
        is_pilot: !!d.is_pilot, license_type: d.license_type || "", notes: d.notes || "",
        parent_organization_id: d.parent_organization_id || "",
      });
    } catch { toast.error("Failed to load organization"); }
  };

  const save = async () => {
    setBusy(true);
    try { await organizationApi.update(detail, form, session); toast.success("Saved"); openDetail(detail); load(); }
    catch { toast.error("Save failed"); } finally { setBusy(false); }
  };
  const assign = async (empId) => {
    try { const r = await organizationApi.assignSales(detail, empId, session); toast.success(`Sales set to ${r.assigned_sales_employee_name || "—"}`); setForm((f) => ({ ...f, assigned_sales_employee_id: empId })); load(); }
    catch { toast.error("Assign failed"); }
  };
  const deactivate = async () => {
    if (!window.confirm("Deactivate this organization?")) return;
    try { await organizationApi.deactivate(detail, session); toast.success("Deactivated"); openDetail(detail); load(); }
    catch { toast.error("Deactivate failed"); }
  };
  const doMerge = async (targetId) => {
    if (!window.confirm("Merge this organization into the selected one? Its schools/portfolios re-link to the target.")) return;
    try { await organizationApi.merge(detail, targetId, session); toast.success("Merged"); setDetail(null); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Merge failed"); }
  };

  // ---- Detail view ----
  if (detail) {
    return (
      <div className="space-y-5" data-testid="org-detail">
        <button onClick={() => setDetail(null)} className="text-sm text-foreground/50 flex items-center gap-1 hover:text-foreground" data-testid="org-back"><ArrowLeft className="h-4 w-4" /> All organizations</button>
        {!detailData || !form ? (
          <div className="flex items-center gap-2 text-sm text-foreground/50 py-10"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
        ) : (
          <>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <h2 className="text-base font-semibold text-foreground flex items-center gap-2"><Building2 className="h-4 w-4 text-primary" /> {detailData.name}</h2>
                <p className="text-xs text-foreground/40">{detailData.type_label} · {detailData.dashboard_label}</p>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => setMergeMode((m) => !m)} data-testid="org-merge-toggle"><GitMerge className="h-3.5 w-3.5 mr-1.5" /> Merge</Button>
                <Button size="sm" variant="outline" onClick={deactivate} data-testid="org-deactivate"><Ban className="h-3.5 w-3.5 mr-1.5" /> Deactivate</Button>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              {[["Schools", detailData.counts.schools, School], ["Users", detailData.counts.users, Users], ["Diagnostics", detailData.counts.diagnostics, ClipboardCheck]].map(([l, v, Icon]) => (
                <div key={l} className="rounded-xl border border-border bg-muted/20 p-3">
                  <Icon className="h-4 w-4 text-foreground/40 mb-1" />
                  <div className="text-2xl font-bold text-foreground">{v}</div>
                  <div className="text-[11.5px] uppercase tracking-wider text-foreground/40">{l}</div>
                </div>
              ))}
            </div>

            {detailData.health && (
              <div className="rounded-xl border border-border p-4" data-testid="org-health">
                <div className="flex items-center gap-2 mb-3">
                  <Activity className="h-4 w-4 text-primary" />
                  <h3 className="text-sm font-semibold text-foreground">Organization health</h3>
                </div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[12px] text-foreground/60">Diagnostic completion</span>
                  <span className="text-[12px] font-semibold text-foreground">
                    {detailData.health.completed_diagnostics}/{detailData.health.total_diagnostics} ({detailData.health.completion_pct}%)
                  </span>
                </div>
                <div className="h-2 w-full rounded-full bg-muted overflow-hidden mb-3">
                  <div className="h-full bg-emerald-500 transition-all" style={{ width: `${Math.min(100, detailData.health.completion_pct)}%` }} />
                </div>
                <div className="grid grid-cols-3 gap-3 text-center">
                  <div>
                    <div className="text-lg font-bold text-foreground">{detailData.health.active_schools}/{detailData.health.schools}</div>
                    <div className="text-[11.5px] uppercase tracking-wider text-foreground/40">Active schools</div>
                  </div>
                  <div>
                    <div className="text-lg font-bold text-foreground">{detailData.health.total_diagnostics}</div>
                    <div className="text-[11.5px] uppercase tracking-wider text-foreground/40">Diagnostics</div>
                  </div>
                  <div>
                    <div className="text-[13px] font-bold text-foreground">{detailData.health.last_activity ? new Date(detailData.health.last_activity).toLocaleDateString() : "—"}</div>
                    <div className="text-[11.5px] uppercase tracking-wider text-foreground/40">Last activity</div>
                  </div>
                </div>
              </div>
            )}

            {mergeMode && (
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-3" data-testid="org-merge-panel">
                <p className="text-[13px] font-medium text-amber-900 mb-2">Merge "{detailData.name}" into:</p>
                <select className="w-full h-9 rounded-md border border-amber-300 bg-white px-2 text-sm" data-testid="org-merge-target"
                  onChange={(e) => e.target.value && doMerge(e.target.value)} defaultValue="">
                  <option value="">Select target organization…</option>
                  {(rows || []).filter((o) => o.id !== detail && o.status !== "merged").map((o) => (
                    <option key={o.id} value={o.id}>{o.name} ({o.type_label})</option>
                  ))}
                </select>
              </div>
            )}

            <div className="rounded-xl border border-border p-4 space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Organization name"><Input data-testid="org-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
                <Field label="Organization type">
                  <select data-testid="org-type" value={form.organization_type} onChange={(e) => setForm({ ...form, organization_type: e.target.value })} className="w-full h-9 rounded-md border border-border bg-background px-2 text-sm">
                    {types.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
                  </select>
                </Field>
                <Field label="State"><Input data-testid="org-state" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} /></Field>
                <Field label="Status">
                  <select data-testid="org-status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="w-full h-9 rounded-md border border-border bg-background px-2 text-sm">
                    <option value="active">Active</option><option value="inactive">Inactive</option>
                  </select>
                </Field>
                <Field label="Website"><Input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} /></Field>
                <Field label="Phone"><PhoneField value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="org-phone" /></Field>
                <Field label="Primary contact name"><Input value={form.primary_contact_name} onChange={(e) => setForm({ ...form, primary_contact_name: e.target.value })} /></Field>
                <Field label="Primary contact email"><Input value={form.primary_contact_email} onChange={(e) => setForm({ ...form, primary_contact_email: e.target.value })} /></Field>
                <Field label="Assigned Sales Employee">
                  <select data-testid="org-sales" value={form.assigned_sales_employee_id || ""} onChange={(e) => assign(e.target.value)} className="w-full h-9 rounded-md border border-border bg-background px-2 text-sm">
                    <option value="">Unassigned</option>
                    {employees.map((e) => <option key={e.id} value={e.id}>{e.first_name || ""} {e.last_name || e.name || e.email}</option>)}
                  </select>
                </Field>
                <Field label="License type"><Input value={form.license_type} onChange={(e) => setForm({ ...form, license_type: e.target.value })} /></Field>
              </div>
              <Field label="Notes"><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
              <label className="flex items-center gap-2 text-sm text-foreground/70"><input type="checkbox" checked={form.is_pilot} onChange={(e) => setForm({ ...form, is_pilot: e.target.checked })} /> Pilot organization</label>
              <Button onClick={save} disabled={busy} data-testid="org-save">{busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />} Save changes</Button>
            </div>
          </>
        )}
      </div>
    );
  }

  // ---- List view ----
  return (
    <div className="space-y-4" data-testid="organizations-admin">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Building2 className="h-5 w-5 text-primary" />
          <div>
            <h1 className="text-base font-semibold text-foreground">Organizations</h1>
            <p className="text-xs text-foreground/40">Every customer as an organization — districts, authorizers, CMOs, single schools.</p>
          </div>
        </div>
        <Button size="sm" variant="outline" data-testid="org-sync" onClick={async () => {
          try { const r = await organizationApi.migrate(session); const c = r.created || {}; toast.success(`Synced (+${(c.portfolios || 0) + (c.tenants || 0)} new)`); load(); }
          catch { toast.error("Sync failed"); }
        }}>Sync from customers</Button>
      </div>

      <div className="flex gap-2 flex-wrap">
        <div className="flex items-center gap-2 rounded-lg border border-border px-3 flex-1 min-w-[220px]">
          <Search className="h-3.5 w-3.5 text-foreground/40" />
          <input data-testid="org-search" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Search organizations…" className="flex-1 bg-transparent py-2 text-sm outline-none" />
        </div>
        <select data-testid="org-type-filter" value={typeFilter} onChange={(e) => { setTypeFilter(e.target.value); }} className="h-10 rounded-lg border border-border bg-background px-2 text-sm">
          <option value="">All types</option>
          {types.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
        </select>
        <Button variant="outline" onClick={load} data-testid="org-apply">Apply</Button>
      </div>

      {rows === null ? (
        <div className="flex items-center gap-2 text-sm text-foreground/50 py-10"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
      ) : rows.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border p-8 text-center text-sm text-foreground/40">No organizations found.</div>
      ) : (
        <div className="rounded-xl border border-border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-foreground/60 text-xs"><tr>
              <th className="text-left px-3 py-2">Organization</th><th className="text-left px-3 py-2">Type</th>
              <th className="text-left px-3 py-2">State</th><th className="text-left px-3 py-2">Status</th><th className="px-3 py-2" />
            </tr></thead>
            <tbody>
              {rows.map((o) => (
                <tr key={o.id} className="border-t border-border hover:bg-muted/30 cursor-pointer" onClick={() => openDetail(o.id)} data-testid={`org-row-${o.id}`}>
                  <td className="px-3 py-2 font-medium text-foreground">{o.name}</td>
                  <td className="px-3 py-2 text-foreground/70">{o.type_label}</td>
                  <td className="px-3 py-2 text-foreground/50">{o.state || "—"}</td>
                  <td className="px-3 py-2"><span className={`text-[11.5px] px-2 py-0.5 rounded-full font-medium ${STATUS[o.status] || "bg-slate-100"}`}>{o.status}</span></td>
                  <td className="px-3 py-2 text-right"><Pencil className="h-3.5 w-3.5 text-foreground/40 inline" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
